/**
 * Workflows authoring + execution UI.
 *
 * Exercises the workflows microservice's three execution endpoints:
 *
 *   POST   /workflows/execute                        — run a workflow
 *   POST   /workflows/executions/{id}/resume         — resume by id
 *   GET    /workflows/executions/{id}                — fetch latest state
 *
 * Layout:
 *   - Left:  Monaco editor for the workflow JSON + a smaller editor for
 *            the trigger payload + Trigger ID input + Execute button.
 *   - Right: Result panel showing status / outputs / final node / error.
 *            When the result is `suspended`, a Resume panel appears with
 *            decision buttons and an optional decision-output editor.
 *
 * State is persisted server-side: the page only holds the most recent
 * ``execution_id``. Resume sends just the decision; the service reloads
 * the trigger snapshot, node outputs, and DSL from MSSQL. A page refresh
 * loses the in-memory result panel but the execution itself survives —
 * a future slice will surface a list of recent executions.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Editor from "@monaco-editor/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  Loader2,
  Play,
  CheckCircle2,
  XCircle,
  Pause,
  ShieldOff,
  Wrench,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "sonner";
import {
  workflowsApi,
  type ExecutionResponse,
  type ExecutionStatus,
} from "@/services/api/workflows";

export const Route = createFileRoute("/portal/workflows/execute")({
  component: WorkflowsPage,
});

// ---------------------------------------------------------------------------
// Default workflow JSON the editor opens with — a small but valid example
// using only logic.branch (no external service deps required to execute).
// ---------------------------------------------------------------------------

const DEFAULT_WORKFLOW = {
  workflow_id: "wf_demo",
  version: 1,
  organisation_id: "00000000-0000-0000-0000-000000000000",
  name: "Branch demo",
  triggers: [
    { id: "trig1", kind: "trigger.manual", entry_node_id: "route" },
  ],
  nodes: [
    {
      id: "route",
      kind: "logic.branch",
      config: {
        branches: [
          {
            name: "high",
            when: "${trigger.payload.amount >= 1000}",
          },
          { name: "low", when: "default" },
        ],
      },
    },
  ],
};

const DEFAULT_PAYLOAD = { amount: 1500 };

const DEFAULT_DECISION_OUTPUT_PLACEHOLDER = `{
  "actor_user_id": "user-xyz",
  "comment": "LGTM"
}`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

function safeParseJson<T = unknown>(raw: string): ParseResult<T> {
  try {
    return { ok: true, value: JSON.parse(raw) as T };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

function statusBadgeVariant(
  status: ExecutionStatus,
): "default" | "destructive" | "secondary" {
  switch (status) {
    case "completed":
      return "default";
    case "failed":
      return "destructive";
    case "completed_with_errors":
    case "suspended":
      return "secondary";
  }
}

function StatusIcon({ status }: { status: ExecutionStatus }) {
  if (status === "completed")
    return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-600" />;
  if (status === "completed_with_errors")
    return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  return <Pause className="h-4 w-4 text-amber-600" />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function WorkflowsPage() {
  const { token } = useAuth();
  const { can } = usePermissions();

  // Workflows are gated by the ``automation`` permission until/unless
  // we add a dedicated ``workflows`` resource to PERMISSION_MATRIX. Two
  // distinct actions:
  // - ``execute`` gates the Execute button + the suspend-resume buttons
  //   (the page is read-only without it)
  // - ``view`` gates the page itself (handled by router-level checks
  //   indirectly via the nav entry, but we also block here as defence
  //   in depth)
  const canView = can("view", "automation");
  const canExecute = can("execute", "automation");

  // Editor state ------------------------------------------------------------
  const [workflowJson, setWorkflowJson] = useState<string>(
    JSON.stringify(DEFAULT_WORKFLOW, null, 2),
  );
  const [payloadJson, setPayloadJson] = useState<string>(
    JSON.stringify(DEFAULT_PAYLOAD, null, 2),
  );
  const [triggerId, setTriggerId] = useState<string>("trig1");

  // Execution state ---------------------------------------------------------
  const [executing, setExecuting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [result, setResult] = useState<ExecutionResponse | null>(null);

  // Decision panel state (only relevant when result.status === 'suspended')
  const [decisionOutputJson, setDecisionOutputJson] = useState<string>("");

  // Parsed workflow and payload — memoised so we don't reparse every render
  const parsedWorkflow = useMemo(() => safeParseJson(workflowJson), [workflowJson]);
  const parsedPayload = useMemo(() => safeParseJson(payloadJson), [payloadJson]);
  const parsedDecisionOutput = useMemo<ParseResult<unknown>>(() => {
    if (!decisionOutputJson.trim()) return { ok: true, value: undefined };
    return safeParseJson(decisionOutputJson);
  }, [decisionOutputJson]);

  const canSubmitExecute =
    canExecute &&
    !executing &&
    parsedWorkflow.ok &&
    parsedPayload.ok &&
    triggerId.trim().length > 0;

  // ---------- handlers ----------

  const handleExecute = async () => {
    if (!parsedWorkflow.ok) {
      toast.error(`Workflow JSON invalid: ${parsedWorkflow.error}`);
      return;
    }
    if (!parsedPayload.ok) {
      toast.error(`Payload JSON invalid: ${parsedPayload.error}`);
      return;
    }
    setExecuting(true);
    setResult(null);
    setDecisionOutputJson("");
    try {
      const response = await workflowsApi.executeWorkflow(
        {
          workflow: parsedWorkflow.value as Record<string, unknown>,
          trigger_id: triggerId,
          payload: parsedPayload.value as Record<string, unknown>,
          // The API now defaults to async (202 + running id). This console
          // renders the result inline, so block for the full result.
          wait: true,
        },
        token ?? undefined,
      );
      setResult(response);
      if (response.status === "suspended") {
        toast.info("Workflow suspended — awaiting human decision");
      } else if (response.status === "completed") {
        toast.success("Workflow completed");
      } else if (response.status === "completed_with_errors") {
        toast.warning("Workflow completed with errors — see result panel");
      } else {
        toast.error("Workflow failed — see result panel");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setExecuting(false);
    }
  };

  const handleDecide = async (decision: string) => {
    if (!result || result.status !== "suspended") return;
    if (!result.execution_id) {
      toast.error(
        "Cannot resume — server did not return an execution id. " +
          "The workflows service is running without persistence.",
      );
      return;
    }
    if (!parsedDecisionOutput.ok) {
      toast.error(`Decision output JSON invalid: ${parsedDecisionOutput.error}`);
      return;
    }

    setResuming(true);
    try {
      const response = await workflowsApi.resumeExecution(
        result.execution_id,
        {
          decision,
          decision_output: parsedDecisionOutput.value as
            | Record<string, unknown>
            | undefined,
        },
        token ?? undefined,
      );
      setResult(response);
      if (response.status === "completed") {
        toast.success(`Resumed — completed via "${decision}"`);
      } else if (response.status === "completed_with_errors") {
        toast.warning("Workflow completed with errors — see result panel");
      } else if (response.status === "failed") {
        toast.error("Resume produced a failed result");
      } else {
        toast.info("Resumed and re-suspended");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setResuming(false);
    }
  };

  // ---------- render ----------

  if (!canView) {
    return (
      <div className="space-y-4 p-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Workflows</h1>
        </div>
        <Card>
          <CardContent className="flex items-center gap-3 py-8">
            <ShieldOff className="h-6 w-6 text-muted-foreground" />
            <div className="text-sm">
              <div className="font-medium">No access</div>
              <div className="text-muted-foreground">
                Your role does not permit viewing workflows. Contact a platform
                admin to request access.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <Alert>
        <Wrench className="h-4 w-4" />
        <AlertDescription>
          <strong>Developer surface.</strong> This page runs raw workflow
          JSON directly against the engine — useful for smoke tests and
          debugging the runner. For day-to-day authoring use the{" "}
          <a className="underline" href="/portal/workflows/definitions">
            Definitions page
          </a>{" "}
          and wire it up via{" "}
          <a className="underline" href="/portal/workflows/subscriptions">
            Subscriptions
          </a>
          .
        </AlertDescription>
      </Alert>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Execute (dev)
        </h1>
        <p className="text-sm text-muted-foreground">
          Author a workflow JSON and execute it against the workflows
          microservice. Suspended executions can be resumed inline below.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ---------------- Authoring side ---------------- */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow definition</CardTitle>
            <CardDescription>
              JSON. See <code>src/workflows/docs/dsl-design.md</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md border">
              <Editor
                height="380px"
                defaultLanguage="json"
                value={workflowJson}
                onChange={(v) => setWorkflowJson(v ?? "")}
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  lineNumbers: "on",
                  tabSize: 2,
                }}
              />
            </div>
            {!parsedWorkflow.ok && (
              <p className="text-xs text-destructive">
                Workflow JSON: {parsedWorkflow.error}
              </p>
            )}

            <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-3">
              <div className="space-y-1 sm:col-span-1">
                <Label htmlFor="trigger-id">Trigger ID</Label>
                <Input
                  id="trigger-id"
                  value={triggerId}
                  onChange={(e) => setTriggerId(e.target.value)}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="payload">Trigger payload</Label>
                <div className="rounded-md border">
                  <Editor
                    height="120px"
                    defaultLanguage="json"
                    value={payloadJson}
                    onChange={(v) => setPayloadJson(v ?? "")}
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 12,
                      lineNumbers: "off",
                      tabSize: 2,
                    }}
                  />
                </div>
                {!parsedPayload.ok && (
                  <p className="text-xs text-destructive">
                    Payload JSON: {parsedPayload.error}
                  </p>
                )}
              </div>
            </div>

            <Button onClick={handleExecute} disabled={!canSubmitExecute}>
              {executing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Execute
            </Button>
            {!canExecute && (
              <p className="text-xs text-muted-foreground">
                Read-only — your role does not permit executing workflows.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ---------------- Result side ---------------- */}
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
            <CardDescription>
              Last execution outcome. Suspended results show a resume panel
              below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!result && (
              <p className="text-sm text-muted-foreground">
                Execute a workflow to see results here.
              </p>
            )}

            {result && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusIcon status={result.status} />
                  <Badge variant={statusBadgeVariant(result.status)}>
                    {result.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {result.steps_taken}{" "}
                    {result.steps_taken === 1 ? "step" : "steps"}
                  </span>
                  {result.execution_id && (
                    <span className="text-xs text-muted-foreground">
                      • id{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
                        {result.execution_id}
                      </code>
                    </span>
                  )}
                </div>

                {result.error && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
                    <div className="font-medium">Error</div>
                    <div className="text-muted-foreground">{result.error}</div>
                  </div>
                )}

                {result.final_node_id && (
                  <div className="text-sm">
                    <span className="font-medium">Final node:</span>{" "}
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {result.final_node_id}
                    </code>
                    {result.final_exit && (
                      <>
                        {" "}via{" "}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {result.final_exit}
                        </code>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <div className="mb-1 text-sm font-medium">Node outputs</div>
                  <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
                    {JSON.stringify(result.node_outputs, null, 2)}
                  </pre>
                </div>

                {result.status === "suspended" && (
                  <div className="space-y-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Pause className="h-4 w-4 text-amber-600" />
                      <span className="font-medium">
                        Suspended at{" "}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {result.suspended_node_id}
                        </code>
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Reason: {result.suspend_reason} • Token:{" "}
                      <code className="rounded bg-muted px-1 py-0.5">
                        {result.suspend_token}
                      </code>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="decision-output">
                        Decision output (optional JSON)
                      </Label>
                      <div className="rounded-md border">
                        <Editor
                          height="100px"
                          defaultLanguage="json"
                          value={decisionOutputJson}
                          onChange={(v) => setDecisionOutputJson(v ?? "")}
                          options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 12,
                            lineNumbers: "off",
                            tabSize: 2,
                            placeholder: DEFAULT_DECISION_OUTPUT_PLACEHOLDER,
                          }}
                        />
                      </div>
                      {!parsedDecisionOutput.ok && (
                        <p className="text-xs text-destructive">
                          Decision output JSON: {parsedDecisionOutput.error}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDecide("approved")}
                        disabled={
                          !canExecute || resuming || !parsedDecisionOutput.ok
                        }
                      >
                        {resuming ? (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-2 h-3 w-3" />
                        )}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDecide("rejected")}
                        disabled={
                          !canExecute || resuming || !parsedDecisionOutput.ok
                        }
                      >
                        <XCircle className="mr-2 h-3 w-3" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDecide("timed_out")}
                        disabled={
                          !canExecute || resuming || !parsedDecisionOutput.ok
                        }
                      >
                        Timed out
                      </Button>
                    </div>
                    {!canExecute && (
                      <p className="text-xs text-muted-foreground">
                        Resume disabled — execute permission required.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
